import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../../styles/loginPage/login.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import Cookies from "js-cookie";
import { setupAxiosInterceptors } from "../../utils/axiosConfig";
import googleIcon from "../../assets/img/google-icon.svg";

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [accountError, setAccountError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [show2FAVerification, setShow2FAVerification] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [userRole, setUserRole] = useState(null);


  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
  if (authSuccess && userRole) {
    redirectToDashboard(userRole);
    setAuthSuccess(false);
    setUserRole(null);
  }
}, [authSuccess, userRole]);

  useEffect(() => {
    setupAxiosInterceptors(navigate);

    const checkAuth = () => {
      const token = Cookies.get("token");
      if (token) {
        const role = Cookies.get("role");
        redirectToDashboard(role);
      }
    };

    checkAuth();
  }, [navigate]);

  const redirectToDashboard = (role) => {
    const routes = {
      SUPER_ADMIN: "/admin/dashboard",
      SCANNER_OPERATOR: "/scanner-operator/dashboard",
      DATA_ENTRY_CLERK: "/data-entry/dashboard",
      CLIENT: "/stakeholder/dashboard",
      PROJECT_COORDINATOR: "/coordinator/dashboard",
      SCANNER_ASSISTANT: "/scanner-assistant/dashboard",
      SUPERVISOR: "/supervisor/dashboard",
    };
    if (routes[role]) navigate(routes[role]);
  };

  const validateForm = () => {
    let isValid = true;
    setAccountError("");
    setPasswordError("");
    setGeneralError("");

    if (!account) {
      setAccountError("Email or Account Name is required");
      isValid = false;
    }

    if (!password) {
      setPasswordError("Password is required");
      isValid = false;
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsSubmitted(false);
    if (name === "account") {
      setAccount(value);
    } else if (name === "password") {
      setPassword(value);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!validateForm()) return;

    setLoading(true);
    try {
      const rememberMe = document.getElementById("remember-me").checked;
      const normalizedAccount = account.toLowerCase();
      const loginData = normalizedAccount.includes("@")
        ? { email: normalizedAccount, password, rememberMe }
        : { name: normalizedAccount, password, rememberMe };
      const res = await axios.post(`${baseUrl}/api/users/login`, loginData);

      if (res.status === 200) {
        if (res.data.requires2FA) {
          setShow2FAVerification(true);
          setTempToken(res.data.tempToken);
          setLoading(false);
          return;
        }

        handleSuccessfulLogin(res.data);
      }
    } catch (err) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = (data) => {
    const { token, refreshToken, user } = data;
    const role = user.role;
    const userName = user.name;
    const email = user.email;
    const tokenTimeStamp = new Date().getTime();

    Cookies.set("token", token, {
      expires: 1,
      secure: true,
      sameSite: "strict",
    });
    Cookies.set("role", role, {
      expires: 1,
      secure: true,
      sameSite: "strict",
    });
    Cookies.set("userName", userName, {
      expires: 1,
      secure: true,
      sameSite: "strict",
    });
    Cookies.set("email", email, {
      expires: 1,
      secure: true,
      sameSite: "strict",
    });
    Cookies.set("tokenTimestamp", tokenTimeStamp, {
      expires: 1,
      secure: true,
      sameSite: "strict",
    });

    // Store 2FA status
    if (user.twoFAEnabled) {
      localStorage.setItem('twoFAEnabled', 'true');
    } else {
      localStorage.setItem('twoFAEnabled', 'false');
    }

    const rememberMe = document.getElementById("remember-me").checked;
    if (refreshToken && rememberMe) {
      Cookies.set("refreshToken", refreshToken, {
        expires: 7,
        secure: true,
        sameSite: "strict",
      });
    } else {
      Cookies.remove("refreshToken");
    }

    redirectToDashboard(user.role);
  };

  const handleLoginError = (err) => {
    if (err.response) {
      if (err.response.status === 401) {
        setGeneralError(
          "Sorry, your credentials are incorrect. Please try again."
        );
      } else {
        setGeneralError("An error occurred. Please try again");
      }
    } else if (err.request) {
      setGeneralError("Server is not responding. Please try again later.");
    } else {
      setGeneralError("An error occurred. Please try again");
    }
  };

  const handle2FAVerification = async () => {
    if (!twoFACode) {
      setGeneralError("Please enter the verification code");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/users/verify-2fa`,
        {
          code: twoFACode
        },
        {
          headers: {
            Authorization: `Bearer ${tempToken}`
          }
        }
      );

      if (res.status === 200) {
        const { token, refreshToken, user } = res.data;
        const role = user.role;
        const userName = user.name;
        const email = user.email;
        const tokenTimeStamp = new Date().getTime();

        Cookies.set("token", token, {
          expires: 1,
          secure: true,
          sameSite: "strict",
        });
        Cookies.set("role", role, {
          expires: 1,
          secure: true,
          sameSite: "strict",
        });
        Cookies.set("userName", userName, {
          expires: 1,
          secure: true,
          sameSite: "strict",
        });
        Cookies.set("email", email, {
          expires: 1,
          secure: true,
          sameSite: "strict",
        });
        Cookies.set("tokenTimestamp", tokenTimeStamp, {
          expires: 1,
          secure: true,
          sameSite: "strict",
        });

        // Store 2FA status
        if (user.twoFAEnabled) {
          localStorage.setItem('twoFAEnabled', 'true');
        } else {
          localStorage.setItem('twoFAEnabled', 'false');
        }

        const rememberMe = document.getElementById("remember-me")?.checked;
        if (refreshToken && rememberMe) {
          Cookies.set("refreshToken", refreshToken, {
            expires: 7,
            secure: true,
            sameSite: "strict",
          });
        }

        setShow2FAVerification(false);
      setTwoFACode("");
      setTempToken(null);
      setGeneralError("");

      setTimeout(() => {
        redirectToDashboard(user.role);
      }, 100);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setGeneralError("Invalid or expired verification code. Please try again.");
      } else {
        setGeneralError("Failed to verify 2FA code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    const uuid = queryParams.get("uuid");
    const email = queryParams.get("email");
    const name = queryParams.get("name");
    const role = queryParams.get("role");

    if (token) {
      const tokenTimeStamp = new Date().getTime();
      Cookies.set("token", token, {
        expires: 1,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("role", role, {
        expires: 1,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("userName", name, {
        expires: 1,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("email", email, {
        expires: 1,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("tokenTimestamp", tokenTimeStamp, {
        expires: 1,
        secure: true,
        sameSite: "strict",
      });
      redirectToDashboard(role);
    }
  }, [location.search, navigate]);

  const handleGoogleLogin = () => {
    const url = `${baseUrl}/api/users/auth/google/login`;
    window.location.href = url;
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="login-grid-container">
        <div className="second-grid-container">
          <div className="second-grid-column">
            {/* <div className="img-container">
              <img src={loginFrame} alt="illustration" />
            </div> */}
            <div className="welcome-message">Welcome to!</div>
            <div className="logo-name title">
              <span>MonitorPro</span>
            </div>
            <div className="description">
              Track your project's journey with precision and clarity.
              MonitorPro empowers teams to achieve goals efficiently. Stay
              connected, informed, and in control.
            </div>
          </div>
          <div className="privacy-policy">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms & Conditions</a>
          </div>
        </div>
        <div className="first-grid-container">
          <div className="logo-name">
            <span>MonitorPro</span>
          </div>
          <div className="first-grid-column">
            {/* <div className="logo-name">MonitorPro</div> */}
            <div className="title">Login Now</div>

            <div className="login-form">
              {!show2FAVerification ? (
                <form onSubmit={handleLogin}>
                  <div className="account">
                    {/* <label for="account-name">Account</label> */}
                    <input
                      name="account"
                      type="text"
                      placeholder="Email or Account name"
                      value={account}
                      onChange={handleChange}
                    />
                    {isSubmitted && accountError && (
                      <div className="error-message">{accountError}</div>
                    )}
                  </div>
                  <div className="form-group password-group">
                    {/* <label for="password">Password</label> */}
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      id="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span
                      className="password-toggle"
                      onClick={handlePasswordToggle}
                      style={{ top: passwordError ? "35%" : "50%" }}
                    >
                      <Icon
                        icon={
                          showPassword ? "iconoir:eye-off" : "iconoir:eye-solid"
                        }
                        width="24px"
                        height="24px"
                        color="var(--login-icon-color)"
                      />
                    </span>
                    {isSubmitted && passwordError && (
                      <div className="error-message">{passwordError}</div>
                    )}
                  </div>

                  {generalError && (
                    <div className="error-message">{generalError}</div>
                  )}

                  <div className="forgot-password">
                    <div className="remember-me-container">
                      <label htmlFor="remember-me">Remember Me</label>
                      <input
                        type="checkbox"
                        id="remember-me"
                        name="remember-me"
                      />
                    </div>

                    <a href="/forgot-password">Forgot Password?</a>
                  </div>

                  <div className="button-group">
                    <button
                      type="submit"
                      className={`login-btn ${loading ? "btn-disabled" : ""} `}
                      disabled={loading}
                    >
                      {loading ? (
                        <AiOutlineLoading3Quarters className="loader" />
                      ) : (
                        "Login"
                      )}
                    </button>
                    <div className="google-btn">
                      <button
                        type="button"
                        className={`google-login-btn ${
                          loading ? "btn-disabled" : ""
                        } `}
                        disabled={loading}
                        onClick={handleGoogleLogin}
                      >
                        <img
                          src={googleIcon}
                          alt="google"
                          className="google-icon"
                        />
                        Login with Google
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="two-fa-verification">
                  <h2>Two-Factor Authentication</h2>
                  <p>Please enter the verification code from your Google Authenticator app</p>
                  <div className="verification-input">
                    <input
                      type="text"
                      placeholder="Enter verification code"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value)}
                    />
                    <button
                      onClick={handle2FAVerification}
                      disabled={loading}
                      className={loading ? "btn-disabled" : ""}
                    >
                      {loading ? (
                        <AiOutlineLoading3Quarters className="loader" />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>
                  {generalError && (
                    <div className="error-message">{generalError}</div>
                  )}
                  <button
                    className="back-button"
                    onClick={() => {
                      setShow2FAVerification(false);
                      setTwoFACode("");
                      setGeneralError("");
                    }}
                  >
                    Back to Login
                  </button>
                </div>
              )}

              <div className="back-office">
                <p>Looking For The Staff area?</p>
                <a href="/signup">Back Office</a>
              </div>
            </div>
          </div>
          <div className="copyright">
            © {new Date().getFullYear()} - AzulPlan. All rights reserved.
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
